import {
  Avatar,
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import Iconify from '../iconify';
import { OptionPreview } from './OptionPreview/index.tsx';
import { useNavigate } from 'react-router-dom';
import { DeleteDialog } from './DeleteDialog';
import { useState } from 'react';
import { orange } from '@mui/material/colors';
import { usePickemStore } from '@/store/usePickem.store.ts';
import { api } from '@/api/api-client.config.ts';
import { QuestionType } from '@/@types/api.types.ts';
import { useAppSearchParams } from '@/hooks/useAppSearchParams.ts';

interface QuestionsTableProps {
  bonusOptionClickHandler?: (questionId: number, optionLabel: string) => void;
  lang: string;
  matchStats?: any;
  showActions: boolean;
  questions: QuestionType[];
  form?: any;
}

export const QuestionsTable = ({
  lang,
  matchStats,
  showActions,
  questions,
  form,
}: QuestionsTableProps) => {
  const { pickemId } = useAppSearchParams();
  const { questionId, setQuestionId } = usePickemStore();

  const navigate = useNavigate();
  const [openConfirm, setOpenConfirm] = useState(false);

  const deleteQuestionHandler = (questionId: number) => {
    api
      .delete(`/admin/pickem-questions/${pickemId}?question_id=${questionId}`)
      .then(() => {
        window.location.reload();
      });
  };

  return (
    <>
      {questions.length === 0 ? (
        <Typography gutterBottom>No questions</Typography>
      ) : (
        <Table sx={{ minWidth: 650 }} aria-label='simple table'>
          <TableBody>
            {questions.map((question, index) => (
              <TableRow
                key={`${lang}-row-${question.question_id}`}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                }}
              >
                <TableCell component='th' scope='row'>
                  <Box sx={{ p: 0, fontSize: 16 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={2}>
                        <Avatar sx={{ bgcolor: orange[500] }}>
                          {index + 1}
                        </Avatar>
                      </Grid>
                      <Grid item xs={10} fontWeight='bold'>
                        {lang ? question[`title_${lang}`] : question.title_en}{' '}
                        {`(${question.points} pts)`}
                      </Grid>
                    </Grid>
                  </Box>
                  <Box sx={{ pt: 2, pb: 2, fontSize: 12 }}>
                    {lang
                      ? question[`description_${lang}`]
                      : question.description_en}
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 5,
                    }}
                  >
                    <Grid container spacing={2}>
                      {question?.custom_value && (
                        <TextField
                          type='number'
                          disabled={true}
                          value={question.custom_value}
                        ></TextField>
                      )}
                      {(question?.options ?? []).map((option, index) => (
                        <Grid item xs={3} key={index}>
                          <OptionPreview
                            option={option}
                            lang={lang}
                            question={question}
                            matchStats={matchStats}
                            form={form}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </TableCell>
                {showActions && (
                  <TableCell align='right'>
                    <Iconify
                      icon='eva:edit-2-outline'
                      onClick={() =>
                        navigate(
                          `/dashboard/pickem/question?pickem_id=${pickemId}&question_id=${question.question_id}`
                        )
                      }
                      sx={{ cursor: 'pointer', margin: 3 }}
                    />
                    <Iconify
                      icon='eva:trash-2-outline'
                      onClick={() => {
                        setOpenConfirm(true);
                        setQuestionId(question.question_id);
                      }}
                      sx={{ cursor: 'pointer', margin: 3 }}
                    />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <DeleteDialog
        openConfirm={openConfirm}
        setOpenConfirm={setOpenConfirm}
        id={questionId}
        deleteHandler={deleteQuestionHandler}
        type={'question'}
      />
    </>
  );
};
